import { Page, Card, Text, Banner, InlineStack, Button } from '@shopify/polaris';
import { useContext } from 'react';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import './index.css';
import { useNavigate } from 'react-router';

export default function NoPermissions({ permission }) {
    const { logout, isCustomerAuthenticated, currentUser } = useContext(SessionContext);
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate('/'); // Redirect to home after logout
    };

    const handleNavigateToPrograms = () => {
        const programsUrl = isCustomerAuthenticated ? '/customer/programs' : '/program-management';
        navigate(programsUrl);
    };

    const handleNavigateToCycleCounts = () => {
        navigate('/cycle-counts');
    };

    const handleNavigateToTransfers = () => {
        const transfersUrl = '/transfers';
        navigate(transfersUrl);
    };

    return (
        <div className="no-permissions-page">
            <Page title="Permissions required">
                <Card sectioned>
                    <InlineStack gap="400" direction="vertical">
                        {!isCustomerAuthenticated && (
                            <>
                                <Text as="p" tone="subdued">
                                    User Email: {currentUser.email}
                                    <br />
                                    User ID: {currentUser.userId}
                                    <br />
                                    {currentUser.role?.roleName ? `Role: ${currentUser.role.roleName}` : 'No role assigned'}
                                </Text>
                                <Text as="p">Your account is missing required permissions. Please contact your administrator to request access.</Text>
                            </>
                        )}
                        {permission == 'role' && (
                            <Banner tone="critical" title="No role assigned">
                                <p>Your user does not have a role configured. An administrator must assign a role to your account.</p>
                            </Banner>
                        )}

                        {permission == 'missing_role' && (
                            <Banner tone="critical" title="Incorrect role assigned">
                                <p>Your user does not have the required permissions to perform that action. An administrator must add the correct permission to your account.</p>
                            </Banner>
                        )}

                        {permission == 'location' && (
                            <Banner tone="critical" title="No locations assigned">
                                <p>Your user has no locations associated. An administrator must add one or more locations to your account.</p>
                            </Banner>
                        )}

                        {permission == 'program' && (
                            <Banner tone="critical" title="No access to this program">
                                <p>You don&apos;t have permission to view this program. If you believe this is a mistake, please contact your administrator.</p>
                            </Banner>
                        )}

                        {permission == 'cycle-count' && (
                            <Banner tone="critical" title="No access to this cycle count">
                                <p>You don&apos;t have permission to access this cycle count. If you believe this is a mistake, please contact your administrator.</p>
                            </Banner>
                        )}

                        {permission == 'transfer' && (
                            <Banner tone="critical" title="No access to this transfer">
                                <p>You don&apos;t have permission to view this transfer. If you believe this is a mistake, please contact your administrator.</p>
                            </Banner>
                        )}

                        {!isCustomerAuthenticated && (
                            <Text as="p" tone="subdued">
                                Once your permissions are updated, sign out and sign back in to continue.
                            </Text>
                        )}
                    </InlineStack>
                    <InlineStack align="end">
                        {permission == 'program' && (
                            <Button onClick={handleNavigateToPrograms} variant="secondary">
                                Go to Programs
                            </Button>
                        )}
                        {permission == 'cycle-count' && (
                            <Button onClick={handleNavigateToCycleCounts} variant="secondary">
                                Go to Cycle Counts
                            </Button>
                        )}
                        {permission == 'transfer' && (
                            <Button onClick={handleNavigateToTransfers} variant="secondary">
                                Go to Transfers
                            </Button>
                        )}
                        <Button onClick={handleLogout} variant="primary">
                            Log out
                        </Button>
                    </InlineStack>
                </Card>
            </Page>
        </div>
    );
}
